import { FC, useCallback, useState } from 'react'

import { FinanceBalanceTicketType } from '@/api/models/finance'
import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { firstPage } from '@/components/pages/bill/model/constants'
import { BillsListItemInfoModal } from '@/components/pages/bill/ui/BillsListItem/BillsListItemInfoModal'
import BillsPayDrawer from '@/components/pages/bill/ui/BillsPayDrawer'
import {
  BillsPayFooterContent,
  BillsPayFooterWrapper
} from '@/components/pages/bill/ui/BillsPayDrawer/BillsPayFooter'
import { ErrorCard, ErrorForbidden } from '@/components/pages/plugs'
import { AppButton } from '@/components/shared/button'
import { AppDrawer } from '@/components/shared/drawer'
import { AppPagination } from '@/components/shared/pagination'
import { ButtonSkeleton, CardSkeleton } from '@/components/shared/skeleton'
import { MODAL_TITLE_ERROR } from '@/utils/constants'
import { isForbiddenError } from '@/utils/isForbiddenError'

import { useInvoiceListDrawer } from '../../hooks/useInvoiceListDrawer'
import { invoiceListDrawerTitle } from '../../model/tickets.constants'

type Props = {
  balance: FinanceBalanceTicketType
  opened?: boolean
  onClose: () => void
  setInvoiceListDrawerOpened: (open: boolean) => void
}

const InvoiceListDrawer: FC<Props> = ({
  balance,
  opened,
  onClose,
  setInvoiceListDrawerOpened
}) => {
  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [
    ScreenSize.Xxs,
    ScreenSize.Xs,
    ScreenSize.Sm
  ].includes(breakpoint)
  const [page, setPage] = useState(firstPage)
  const [billsPayDrawerOpened, setBillsPayDrawerOpened] = useState(false)

  const {
    invoices,
    invoiceIds,
    totalPages,
    isFetching,
    isError,
    error,
    isLoading
  } = useInvoiceListDrawer({
    merchantId: balance.merchId,
    page
  })

  const handleInvoiceListDrawerClose = useCallback(() => {
    setPage(firstPage)
    onClose()
  }, [onClose])

  if (isForbiddenError(error)) {
    return <ErrorForbidden />
  }

  const onClickBillsPayDrawerOpened = () => {
    setBillsPayDrawerOpened(true)
    handleInvoiceListDrawerClose()
  }

  const getTitle = () => {
    if (isLoading) {
      return (
        <ButtonSkeleton className="h-[44px] w-[484px] sm:h-[28px] sm:w-[269px]" />
      )
    }
    if (isError) {
      return MODAL_TITLE_ERROR
    }

    return invoiceListDrawerTitle[balance.type]
  }

  return (
    <>
      <AppDrawer
        theme="white"
        title={getTitle()}
        opened={opened || false}
        onClose={handleInvoiceListDrawerClose}
      >
        {isLoading && !isError && (
          <div className="flex h-full flex-col">
            <div className="flex flex-col">
              {Array.from({ length: 4 }).map((_, index) => (
                <CardSkeleton
                  key={index}
                  className="my-8"
                  height={isMobileScreen ? 152 : 168}
                />
              ))}
            </div>
            <div className="mb-[40px] mt-auto pt-8">
              <hr className="text-grey-30" />
              <div className="mt-20 flex items-end justify-between sm:flex-col sm:items-start">
                <div className="flex flex-col gap-12 sm:mb-12 sm:w-full">
                  {!isMobileScreen && (
                    <>
                      <ButtonSkeleton className="h-[20px] w-[130px]" />
                      <ButtonSkeleton className="h-[36px] w-[188px]" />
                      <ButtonSkeleton className="h-[20px] w-[160px]" />
                    </>
                  )}
                  {isMobileScreen && (
                    <div className="flex w-full items-end justify-between">
                      <div className="flex flex-col gap-4">
                        <ButtonSkeleton className="h-[20px] w-[100px]" />
                        <ButtonSkeleton className="h-[20px] w-[168px]" />
                      </div>
                      <ButtonSkeleton className="h-[32px] w-[120px]" />
                    </div>
                  )}
                </div>
                <ButtonSkeleton className="h-[56px] w-[200px] sm:w-full" />
              </div>
            </div>
          </div>
        )}
        {!isLoading && isError && (
          <div className="flex h-full flex-col">
            <div className="mt-[22%]">
              <ErrorCard customDescription="Попробуйте повторить попытку" />
            </div>

            <AppButton
              fluid
              size={isMobileScreen ? 'small' : 'large'}
              onClick={handleInvoiceListDrawerClose}
              className="mb-[40px] mt-auto pt-8"
              variant="secondary"
            >
              Понятно
            </AppButton>
          </div>
        )}
        {!isLoading && !isError && (
          <>
            <div className="flex-grow overflow-y-auto overflow-x-hidden">
              <BillsListItemInfoModal invoices={invoices} />
              <AppPagination
                page={page}
                totalPages={totalPages ?? 0}
                isFetching={isFetching}
                className="mt-24 xs:mt-16"
                onPageChange={setPage}
              />
            </div>
            <BillsPayFooterWrapper className="px-0 lg:px-0">
              <BillsPayFooterContent
                isCommonPayment
                debtSum={balance.debtSum}
                buttonText="Далее"
                onButtonClick={onClickBillsPayDrawerOpened}
              />
            </BillsPayFooterWrapper>
          </>
        )}
      </AppDrawer>

      <BillsPayDrawer
        isCommonPayment
        invoice={balance}
        invoiceIds={invoiceIds}
        opened={billsPayDrawerOpened}
        onClose={() => setBillsPayDrawerOpened(false)}
        setInvoiceListDrawerOpened={setInvoiceListDrawerOpened}
      />
    </>
  )
}

export default InvoiceListDrawer
